import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Instagram, MagicStar, ShieldTick, TickCircle } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { InstagramFeatureItem, SefanoButton } from "@/components/Molecules";
import { formatPersianNumber } from "@/lib/format/persian";
import type { InstagramConnectHeroProps } from "./InstagramConnectHero.types";
import * as styles from "./InstagramConnectHero.styles";

export type { InstagramConnectHeroProps } from "./InstagramConnectHero.types";

export function InstagramConnectHero({
  features,
  stepKeys,
  requirementKeys,
  isRedirecting,
  onConnect,
  canManage,
  variant = "panel",
}: InstagramConnectHeroProps) {
  const { t } = useTranslation("instagram");
  const theme = useTheme();

  return (
    <Box sx={styles.root(variant)}>
      <Box sx={styles.heroCard}>
        <Box sx={styles.logoBubble}>
          <Instagram size={34} variant="Bold" color="currentColor" />
        </Box>
        <Box component="span" sx={styles.badge}>
          <MagicStar size={14} variant="Bold" color="currentColor" />
          {t("badge")}
        </Box>
        <Typography component="h2" sx={styles.title}>
          {t("hero.title")}
        </Typography>
        <Typography sx={styles.description}>{t("hero.description")}</Typography>

        <SefanoButton
          onClick={onConnect}
          disabled={!canManage || isRedirecting}
          startIcon={<Instagram size={20} variant="Bold" color={theme.palette.common.white} />}
          sx={styles.cta}
        >
          {isRedirecting ? t("hero.redirecting") : t("hero.cta")}
        </SefanoButton>

        <Typography sx={styles.secureNote}>
          <ShieldTick size={16} variant="Bold" color={theme.palette.success.main} />
          {canManage ? t("hero.secure") : t("hero.noPermission")}
        </Typography>

        <Box sx={styles.steps}>
          {stepKeys.map((stepKey, index) => (
            <Box key={stepKey} sx={styles.step}>
              <Box component="span" sx={styles.stepNumber}>
                {formatPersianNumber(index + 1)}
              </Box>
              {t(stepKey)}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={styles.sideColumn}>
        <Box sx={styles.sectionCard}>
          <Typography sx={styles.sectionTitle}>{t("features.title")}</Typography>
          <Box sx={styles.featureGrid}>
            {features.map((feature) => (
              <InstagramFeatureItem
                key={feature.key}
                icon={feature.icon}
                title={t(feature.titleKey)}
                description={t(feature.descriptionKey)}
              />
            ))}
          </Box>
          <Typography sx={styles.sectionNote}>{t("features.rolloutNote")}</Typography>
        </Box>

        <Box sx={styles.sectionCard}>
          <Typography sx={styles.sectionTitle}>{t("requirements.title")}</Typography>
          {requirementKeys.map((requirementKey) => (
            <Typography key={requirementKey} sx={styles.requirement}>
              <TickCircle size={18} variant="Bold" color={theme.palette.primary.main} />
              {t(requirementKey)}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
